/**
 * React dependencies
 */

import React from 'react';

/**
 * DWT dependencies
 */

import { arrowRightIcon } from '../img/icons';
import { decodeHtmlEntities } from './utils/component-utils';

/**
 * Article
 *
 * A component to render a single WordPress article
 *
 * @param {Object}  props
 * @param {Array}   props.articleData
 * @param {Boolean} props.endReached
 *
 */

const Article = ({ articleData, endReached }) => {
  // Check if this is a team member post type
  const isTeamMember = articleData.subtype === 'team_member';

  // Handle featured image - for team members, use ACF field
  let featuredImage = '';
  let featuredImageAlt = '';

  if ( isTeamMember ) {
    // Team member photo from ACF
    featuredImage = articleData.ACF?.team_member_photo?.url || '';
    featuredImageAlt = articleData.ACF?.team_member_name || articleData.title || '';
  } else {
    // Regular post/page featured image
    featuredImage = articleData._embedded?.self?.[0]?.image?.url || '';
    featuredImageAlt = articleData._embedded?.self?.[0]?.image?.alt || '';
  }

  // Handle excerpt - for team members, use bio field
  let excerpt = '';
  if ( isTeamMember ) {
    excerpt = articleData.ACF?.team_member_bio || '';
  } else {
    excerpt = articleData._embedded?.self[0]?.excerpt?.rendered || '';
  }

  // Handle title display - for team members, show name and job title
  let displayTitle = articleData.title;
  if ( isTeamMember && articleData.ACF?.team_member_name ) {
    displayTitle = articleData.ACF.team_member_name;
    if ( articleData.ACF?.job_title ) {
      displayTitle += ` - ${articleData.ACF.job_title}`;
    }
  }

  return (
    <div className={`py-30 border-b-[0.5px] border-dark-gray flex flex-col md:flex-row ${endReached ? 'last:border-b-0' : ''}`}>
      {featuredImage && (
        <div className={`h-40 min-h-40 min-w-40 bg-placeholder-gray mr-20 mb-30 md:mb-0 relative overflow-hidden ${isTeamMember ? 'w-32 aspect-[81/100]' : 'w-40'}`}>
          <img className="object-cover object-center absolute inset-0 w-full h-full" src={featuredImage} alt={featuredImageAlt} />
        </div>
      )}
      <li>
        <a href={ articleData.url } className="no-underline"><strong><p className="underline underline-offset-4 font-bold text-18 mt-0" dangerouslySetInnerHTML={ { __html: displayTitle } } /></strong></a>

        {excerpt && (
          <p className="article-result-text paragraph-2 my-15 line-clamp-5 md:line-clamp-4 text-ellipsis" dangerouslySetInnerHTML={ { __html: excerpt } } />
        )}

        <div className="flex items-center">
          <a 
            href={ articleData.url } 
            className="no-underline hover:underline underline-offset-4 font-medium mr-10"
            aria-label={`${isTeamMember ? 'View Profile' : 'Read More'} about ${decodeHtmlEntities( articleData.ACF?.team_member_name || articleData.title )}`}
          >
            {isTeamMember ? 'View Profile' : 'Read More'}
          </a>
          {arrowRightIcon}
        </div>
      </li>
    </div>
  );
};

export default Article;
